import React from 'react';
import PropTypes from 'prop-types';
import { Box, Paper } from '@mui/material';
import MDBox from 'components/MDBox';
import MDTypography from 'components/MDTypography';

import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';

/**
 * Log mesajlarını gösteren bileşen
 */
function LogDisplay({ logs = [], theme }) {
    // Log tiplerine göre renk ve ikonları ayarla
    const logColors = {
        info: theme?.colors?.status?.info || '#3498db',
        success: theme?.colors?.status?.success || '#10B981',
        warning: theme?.colors?.status?.warning || '#FBBF24',
        error: theme?.colors?.status?.error || '#EF4444',
    };

    // Log tiplerine göre ikon bileşenleri
    const getIconForType = (type) => {
        switch (type) {
            case 'info':
                return <InfoOutlinedIcon sx={{ color: logColors.info, fontSize: 20 }} />;
            case 'success':
                return <CheckCircleOutlineIcon sx={{ color: logColors.success, fontSize: 20 }} />;
            case 'warning':
                return <WarningAmberIcon sx={{ color: logColors.warning, fontSize: 20 }} />;
            case 'error':
                return <ErrorOutlineIcon sx={{ color: logColors.error, fontSize: 20 }} />;
            default:
                return <InfoOutlinedIcon sx={{ color: logColors.info, fontSize: 20 }} />;
        }
    };

    // Log tiplerine göre başlık renkleri
    const getColorForType = (type) => {
        switch (type) {
            case 'info':
                return logColors.info;
            case 'success':
                return logColors.success;
            case 'warning':
                return logColors.warning;
            case 'error':
                return logColors.error;
            default:
                return logColors.info;
        }
    };

    // Bir mesaj için süre formatla (yalnızca bağıl saat)
    const formatTimestamp = (timestamp) => {
        const date = new Date(timestamp);
        return date.toLocaleTimeString('tr-TR', {
            hour: '2-digit',
            minute: '2-digit',
            second: '2-digit',
        });
    };

    if (!logs || logs.length === 0) {
        return (
            <MDBox
                sx={{
                    p: 3,
                    borderRadius: '8px',
                    backgroundColor: '#f8f9fa',
                    textAlign: 'center',
                    border: '1px dashed #cfd8dc',
                }}
            >
                <MDTypography
                    variant="subtitle2"
                    sx={{
                        color: theme?.colors?.text?.secondary || '#64748B',
                        fontSize: '0.9375rem',
                        fontFamily: theme?.fontFamily,
                    }}
                >
                    Henüz log mesajı bulunmamaktadır.
                </MDTypography>
            </MDBox>
        );
    }

    return (
        <Box sx={{ width: '100%' }}>
            {logs.map((log, index) => (
                <Paper
                    key={`${log.timestamp}-${index}`}
                    elevation={0}
                    sx={{
                        mb: 1.5,
                        p: 1.5,
                        borderRadius: '6px',
                        backgroundColor: '#FFFFFF',
                        border: `1px solid ${theme?.colors?.background?.dark || '#E2E8F0'}`,
                    }}
                >
                    <Box
                        sx={{
                            display: "flex",
                            alignItems: "flex-start"
                        }}>
                        <Box
                            sx={{
                                mr: 1.5,
                                mt: 0.25
                            }}>
                            {getIconForType(log.type)}
                        </Box>
                        <Box sx={{
                            flex: 1
                        }}>
                            <Box
                                sx={{
                                    display: "flex",
                                    justifyContent: "space-between",
                                    alignItems: "center",
                                    mb: 0.75
                                }}>
                                <MDTypography
                                    variant="body2"
                                    sx={{
                                        fontSize: '0.875rem',
                                        fontWeight: 600,
                                        color: getColorForType(log.type),
                                        fontFamily: theme?.fontFamily,
                                    }}
                                >
                                    {log.type.charAt(0).toUpperCase() + log.type.slice(1)}
                                </MDTypography>
                                <MDTypography
                                    variant="caption"
                                    sx={{
                                        fontSize: '0.75rem',
                                        color: theme?.colors?.text?.secondary || '#64748B',
                                        fontFamily: theme?.fontFamily,
                                    }}
                                >
                                    {formatTimestamp(log.timestamp)}
                                </MDTypography>
                            </Box>
                            <MDTypography
                                variant="body2"
                                sx={{
                                    fontSize: '0.9375rem',
                                    color: theme?.colors?.text?.primary || '#1E293B',
                                    fontFamily: theme?.fontFamily,
                                    lineHeight: 1.5,
                                    whiteSpace: 'pre-wrap',
                                }}
                            >
                                {log.message}
                            </MDTypography>
                        </Box>
                    </Box>
                </Paper>
            ))}
        </Box>
    );
}

// PropTypes
LogDisplay.propTypes = {
    logs: PropTypes.array,
    theme: PropTypes.object,
};

export default LogDisplay;
